import React, { Suspense, lazy, useState, useMemo, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import Login from './components/Login';
import {
  Box,
  CircularProgress,
  ThemeProvider,
  CssBaseline,
  useMediaQuery,
  Typography,
  Paper
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { getTheme } from './theme'; // [MODIFIED] Import theme factory
import { SnackbarProvider } from 'notistack'; // [NEW] Notification Provider
import { SystemHealthProvider } from './contexts/SystemHealthContext'; // [NEW] Import

// [NEW] Theme mode context for dark mode toggle
export const ThemeModeContext = createContext({ toggleTheme: () => { }, mode: 'light' });

// Lazy load components
const Dashboard = lazy(() => import('./components/Dashboard'));
const Changelog = lazy(() => import('./components/Changelog'));


// Sub-component to handle screen size check & Routing
const AppContent = () => {
  const theme = useTheme();
  // Check if screen is small (mobile phone size, < 600px). Returns true if mobile.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (isMobile) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'background.default',
          p: 3,
          textAlign: 'center'
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 5,
            borderRadius: 4,
            maxWidth: 400,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: 'rgba(211, 47, 47, 0.1)', // Light Error Red
            color: 'error.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3
          }}>
            <PhoneAndroidIcon sx={{ fontSize: 40 }} />
          </Box>
          <Typography variant="h5" fontWeight="800" gutterBottom>
            Desktop or Tablet Required
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            This admin dashboard handles complex data and is optimized for larger screens.
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Please switch to a tablet, laptop, or desktop computer to access the system.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <AuthProvider>
      <Router>
        <Box sx={{ flexGrow: 1, minHeight: '100vh', bgcolor: 'background.default' }}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/*"
              element={
                <PrivateRoute>
                  <Suspense fallback={
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                      <CircularProgress color="primary" />
                    </Box>
                  }>
                    <Routes>
                      <Route path="/*" element={<Dashboard />} />
                    </Routes>
                  </Suspense>
                </PrivateRoute>
              }
            />
          </Routes>
        </Box>
      </Router>
    </AuthProvider>
  );
};



// ... (rest of imports)

function App() {
  // Theme mode with system preference detection
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem('themeMode');
    if (saved) return saved;
    // Default to system preference
    return prefersDarkMode ? 'dark' : 'light';
  });

  const toggleTheme = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('themeMode', newMode);
  };

  const theme = useMemo(() => getTheme(mode), [mode]);

  return (
    <ThemeModeContext.Provider value={{ toggleTheme, mode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <SystemHealthProvider>
            <AppContent />
          </SystemHealthProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export default App;